import React, { memo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { submitReportDirect } from "../../api/reports";
import { getAuthToken } from "../../api/auth";
import { useDataContext } from "../../contexts/DataContext";
import { uploadImagesDirect, getUploadErrorMessage } from "../../api/uploads";
import LocationPickerModal from "./LocationPickerModal";
import "../styles/ReportPage.css";

interface ReportPageProps {
  files: File[];
  onClose: () => void;
  onSuccess: () => void;
}

// Trash types matching cleancy mobile app
const getTrashTypes = (t: any) => [
  {
    value: 1,
    label: t("trash.domestic"),
    icon: `${process.env.PUBLIC_URL}/images/domestic.png`,
  },
  {
    value: 2,
    label: t("trash.construction"),
    icon: `${process.env.PUBLIC_URL}/images/construction.png`,
  },
  {
    value: 3,
    label: t("trash.hazardous"),
    icon: `${process.env.PUBLIC_URL}/images/dangerous.png`,
  },
  {
    value: 4,
    label: t("trash.liquid"),
    icon: `${process.env.PUBLIC_URL}/images/liquid.png`,
  },
];

// Pollution area options matching cleancy (100m, 500m, 1km)
const getPollutionAreas = (t: any) => [
  { value: 1, label: t("pollution.area100m") },
  { value: 2, label: t("pollution.area500m") },
  { value: 3, label: t("pollution.area1km") },
];

// Action plan options matching cleancy
const getActionPlans = (t: any) => [
  { value: 1, label: t("actionPlan.needHelp") },
  { value: 0, label: t("actionPlan.cleanMyself") },
  { value: 2, label: t("actionPlan.groupCleanup") },
];

const ReportPage: React.FC<ReportPageProps> = memo(
  ({ files, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const { updateData } = useDataContext();
    const [comment, setComment] = useState("");
    const [amount, setAmount] = useState<number>(1); // Slider from 1-40
    const [area, setArea] = useState<number>(1);
    const [trashTypes, setTrashTypes] = useState<number[]>([1]);
    const [action, setAction] = useState<number>(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [location, setLocation] = useState<{
      latitude: number;
      longitude: number;
    } | null>(null);
    const [locationName, setLocationName] = useState("");
    const [agreeToRules, setAgreeToRules] = useState(true);
    const [locationError, setLocationError] = useState(false);
    const [showLocationPicker, setShowLocationPicker] = useState(false);

    // Image previews
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);

    // Create blob URLs for image previews
    useEffect(() => {
      const urls = files.map((file) => URL.createObjectURL(file));
      setImagePreviews(urls);

      // Clean up object URLs when component unmounts or files change
      return () => {
        urls.forEach((url) => URL.revokeObjectURL(url));
      };
    }, [files]);

    // Get current location
    useEffect(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            setLocationError(false);

            // Reverse geocode to get location name
            fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`,
            )
              .then((res) => res.json())
              .then((data) => {
                setLocationName(
                  data.display_name || t("report.unknownLocation"),
                );
              })
              .catch(() => {
                setLocationName(t("report.unknownLocation"));
              });
          },
          (err) => {
            console.error("Error getting location:", err);
            setLocationError(true);
          },
        );
      } else {
        setLocationError(true);
      }
    }, [t]);

    const handleTrashTypeToggle = useCallback((value: number) => {
      setTrashTypes((prev) =>
        prev.includes(value)
          ? prev.filter((t) => t !== value)
          : [...prev, value],
      );
    }, []);

    const handleLocationSelected = useCallback(
      (newLocation: { latitude: number; longitude: number }) => {
        setLocation(newLocation);
        setLocationError(false);
        setError(null);

        // Reverse geocode
        fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLocation.latitude}&lon=${newLocation.longitude}`,
        )
          .then((res) => res.json())
          .then((data) => {
            setLocationName(data.display_name || t("report.unknownLocation"));
          })
          .catch(() => {
            setLocationName(t("report.unknownLocation"));
          });
      },
      [t],
    );

    const handleSubmit = useCallback(async () => {
      const token = getAuthToken();
      if (!token || loading || !agreeToRules) return;

      if (!location) {
        setError(t("report.locationRequired"));
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Images go straight to GCS via signed URLs; we submit only the keys.
        const imageKeys = await uploadImagesDirect(files, token);

        await submitReportDirect(
          {
            imageKeys,
            comment,
            geoposition: JSON.stringify({
              latitude: location.latitude,
              longitude: location.longitude,
            }),
            locationName,
            amount,
            area,
            trashTypes: JSON.stringify(trashTypes),
            action,
          },
          token,
        );

        // Refresh reports list
        await updateData();

        onSuccess();
      } catch (err) {
        setError(getUploadErrorMessage(err, t));
        console.error("Failed to submit report:", err);
      } finally {
        setLoading(false);
      }
    }, [
      loading,
      agreeToRules,
      location,
      files,
      comment,
      amount,
      area,
      trashTypes,
      action,
      locationName,
      updateData,
      onSuccess,
      t,
    ]);

    const TRASH_TYPES = getTrashTypes(t);
    const POLLUTION_AREAS = getPollutionAreas(t);
    const ACTION_PLANS = getActionPlans(t);
    const minimumVolumeActionWarningKey =
      action === 1
        ? "report.minimumReportNeedHelpActionWarning"
        : "report.minimumReportCleanupActionWarning";

    // Get pollution level icon based on amount
    const getPollutionIcon = () => {
      if (amount < 10) return `${process.env.PUBLIC_URL}/images/trash0.png`;
      if (amount < 20) return `${process.env.PUBLIC_URL}/images/trash1.png`;
      return `${process.env.PUBLIC_URL}/images/trash2.png`;
    };

    return (
      <div className="report-page-overlay">
        <div className="report-page">
          <div className="report-page-header">
            <h2>{t("report.newReport")}</h2>
            <button className="report-close-btn" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="report-page-content">
            {/* Image Preview */}
            <div className="report-images-section">
              <div className="report-images-grid">
                {imagePreviews.map((url, index) => (
                  <img
                    key={index}
                    src={url}
                    alt={`Preview ${index + 1}`}
                    className="report-image-preview"
                  />
                ))}
              </div>
            </div>

            {/* Minimum Volume Warning */}
            <div className="report-warning">
              <div className="report-warning-icon">!</div>
              <div className="report-warning-content">
                <div className="report-warning-title">
                  {t("report.minimumReportVolumeTitle")}
                </div>
                <p>{t("report.minimumReportVolumeWarning")}</p>
              </div>
            </div>

            {/* Location Info */}
            <div className="report-section">
              <label className="report-label">{t("report.location")}</label>
              {locationError && !location ? (
                <div className="manual-location-section">
                  <p className="location-error-text">
                    {t("report.locationUnavailable")}
                  </p>
                  <button
                    onClick={() => setShowLocationPicker(true)}
                    className="set-location-btn"
                    disabled={loading}
                  >
                    {t("report.setLocationManually")}
                  </button>
                </div>
              ) : null}
              <div className="report-location-info">
                {location ? (
                  <>
                    <p className="location-coords">
                      {location.latitude.toFixed(6)},{" "}
                      {location.longitude.toFixed(6)}
                    </p>
                    <p className="location-name">{locationName}</p>
                    {locationError && (
                      <button
                        onClick={() => setShowLocationPicker(true)}
                        className="change-location-btn"
                        disabled={loading}
                      >
                        {t("report.changeLocation")}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="location-loading">
                    {t("report.gettingLocation")}
                  </p>
                )}
              </div>
            </div>

            {/* Comment */}
            <div className="report-section">
              <label htmlFor="comment" className="report-label">
                {t("report.commentOptional")}
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("report.commentPlaceholder")}
                className="report-textarea"
                rows={3}
                maxLength={300}
                disabled={loading}
              />
            </div>

            {/* Action Plan */}
            <div className="report-section">
              <label className="report-label">
                {t("actionPlan.actionPlan")}
              </label>
              <div className="action-plan-radio">
                {ACTION_PLANS.map((plan) => (
                  <button
                    key={plan.value}
                    className={`action-plan-option ${
                      action === plan.value ? "active" : ""
                    }`}
                    onClick={() => setAction(plan.value)}
                    disabled={loading}
                  >
                    <div className="radio-circle">
                      {action === plan.value && <div className="radio-dot" />}
                    </div>
                    <span>{plan.label}</span>
                  </button>
                ))}
              </div>
              <div className="report-action-warning">
                <p>{t(minimumVolumeActionWarningKey)}</p>
              </div>
            </div>

            {/* Pollution Level (Slider) */}
            <div className="report-section">
              <label className="report-label">
                {t("pollution.pollutionLevel")}
              </label>
              <p className="report-description">
                {t("pollution.pollutionLevelEstimate")}
              </p>
              <div className="pollution-level-display">
                <span className="pollution-amount-value">{amount}</span>
                <img
                  src={getPollutionIcon()}
                  alt="Pollution level"
                  className="pollution-icon"
                />
              </div>
              <input
                type="range"
                min="1"
                max="40"
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value))}
                className="pollution-slider"
                disabled={loading}
              />
            </div>

            {/* Pollution Area */}
            <div className="report-section">
              <label className="report-label">
                {t("pollution.pollutionArea")}
              </label>
              <div className="report-options-grid area-options">
                {POLLUTION_AREAS.map((areaOption) => (
                  <button
                    key={areaOption.value}
                    className={`report-option-btn ${
                      area === areaOption.value ? "active" : ""
                    }`}
                    onClick={() => setArea(areaOption.value)}
                    disabled={loading}
                  >
                    {areaOption.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Trash Types */}
            <div className="report-section">
              <label className="report-label">{t("trash.typeOfTrash")}</label>
              <div className="trash-types-grid">
                {TRASH_TYPES.map((type) => (
                  <button
                    key={type.value}
                    className={`trash-type-btn ${
                      trashTypes.includes(type.value) ? "active" : ""
                    }`}
                    onClick={() => handleTrashTypeToggle(type.value)}
                    disabled={loading}
                  >
                    <img
                      src={type.icon}
                      alt={type.label}
                      className="trash-type-icon"
                    />
                    <span className="trash-type-label">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Rules Agreement */}
            <div className="report-section">
              <label className="report-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreeToRules}
                  onChange={(e) => setAgreeToRules(e.target.checked)}
                  disabled={loading}
                />
                <span>{t("rules.agreeToTerms")}</span>
              </label>
            </div>

            {error && <div className="report-error">{error}</div>}
          </div>

          <div className="report-page-footer">
            <button
              className="report-cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              {t("common.cancel")}
            </button>
            <button
              className="report-submit-btn"
              onClick={handleSubmit}
              disabled={loading || !agreeToRules || !location}
            >
              {loading ? t("report.submitting") : t("report.submitReport")}
            </button>
          </div>
        </div>

        {/* Location Picker Modal */}
        {showLocationPicker && (
          <LocationPickerModal
            onClose={() => setShowLocationPicker(false)}
            onLocationSelected={handleLocationSelected}
            initialLocation={location || undefined}
          />
        )}
      </div>
    );
  },
);

ReportPage.displayName = "ReportPage";

export default ReportPage;
